/**
 * The ActivityOverview-Component displays all CRM activities
 * in a table with search, filtering and sorting.
 */

import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore, FilterList, FilterListOff, RestartAlt, UnfoldMore } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { doesPermissionsHaveSomeOf, doesRolesHaveSomeOf } from "../../utils/authUtils";
import { MemberPartialDto, MembersFieldDto } from "../../types/membersTypes";
import {
  ActivityDto,
  ActivityRecurrenceEndType,
  ActivityRecurrenceFrequency,
  ActivityResultType,
  ActivityStatus,
  ActivityType,
  CreateActivityDto,
} from "../../types/activityTypes";
import ActivityActionButtons from "../../components/activities/ActivityActionButtons";
import ActivityAddButton from "../../components/activities/ActivityAddButton";
import ActivityCompleteDialog from "../../components/activities/ActivityCompleteDialog";
import ActivityFormDialog from "../../components/activities/ActivityFormDialog";
import ActivityStatusChip from "../../components/activities/ActivityStatusChip";
import { getActivityTypeLabel } from "../../components/activities/activityLabels";
import ConfirmationDialog from "../../components/general/ConfirmationDialog";
import useActivities from "../../hooks/activities/useActivities";
import useActivityMutations from "../../hooks/activities/useActivityMutations";
import useContactPersons from "../../hooks/contactPersons/useContactPersons";
import useMembers from "../../hooks/members/useMembers";

const CRM_PERMISSION_ID = 13;
const HOCR_DIRECTOR_ID = 44;

const StyledForm = styled("form")(() => ({
  width: "100%",
}));

const SearchField = styled(TextField)(() => ({
  width: "100%",
}));

const StyledLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline;
  }
`;

type ActivityDialogMode = "create" | "edit" | "complete";

const ActivityOverview: React.FunctionComponent = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const hasActivityManagementPermission =
    doesPermissionsHaveSomeOf(auth.permissions, [CRM_PERMISSION_ID]) ||
    doesRolesHaveSomeOf(auth.roles, [HOCR_DIRECTOR_ID]);

  const styles = {
    amountOfEntries: {
      marginBottom: "10px",
      padding: "7px",
      textAlign: "center",
      fontWeight: "bold",
    },
    filterContainer: {
      marginBottom: "10px",
      padding: "16px",
    },
    filterToolbar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: "16px",
      flexWrap: "wrap",
    },
    filterActions: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    filterPanel: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
      width: "100%",
      marginTop: "8px",
      alignItems: "flex-start",
      padding: "20px",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
    },
    filterSection: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    filterLabel: {
      fontWeight: 400,
    },
    filterOptions: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      gap: "8px 20px",
    },
    searchField: {
      [theme.breakpoints.up("md")]: {
        margin: "7px",
        width: "155px",
      },
      [theme.breakpoints.down("xl")]: {
        margin: "7px",
        width: "120px",
      },
    },
    tableContainer: {
      maxHeight: "calc(100vh - 310px)",
    },
    tableHeadCell: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    tableHeadSortBtn: {
      display: "flex",
      alignItems: "center",
    },
    actionCell: {
      whiteSpace: "nowrap",
      textAlign: "right",
    },
    actionButtons: {
      display: "flex",
      gap: "8px",
      alignItems: "center",
      justifyContent: "flex-end",
    },
  };

  const [statusFilter, setStatusFilter] = useState<ActivityStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<ActivityType[]>([]);
  const [deadlineFilter, setDeadlineFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<string>("deadlineAsc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<ActivityDialogMode>("create");
  const [selectedActivity, setSelectedActivity] = useState<ActivityDto | null>(null);
  const [selectedContactPersonId, setSelectedContactPersonId] = useState<number | "">("");
  const [activityType, setActivityType] = useState<ActivityType>(ActivityType.CALL);
  const [activityTitle, setActivityTitle] = useState("");
  const [activityDescription, setActivityDescription] = useState("");
  const [selectedResponsibleUserId, setSelectedResponsibleUserId] = useState<number | "">("");
  const [activityDeadline, setActivityDeadline] = useState<Dayjs | null>(null);
  const [activityStatus, setActivityStatus] = useState<ActivityStatus>(ActivityStatus.OPEN);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<ActivityRecurrenceFrequency>(
    ActivityRecurrenceFrequency.MONTHLY
  );
  const [recurrenceEndType, setRecurrenceEndType] = useState<ActivityRecurrenceEndType>(
    ActivityRecurrenceEndType.AFTER_N_OCCURRENCES
  );
  const [recurrenceCount, setRecurrenceCount] = useState(3);
  const [resultType, setResultType] = useState<ActivityResultType | "">("");
  const [resultComment, setResultComment] = useState("");
  const [activityTypeError, setActivityTypeError] = useState(false);
  const [contactPersonError, setContactPersonError] = useState(false);
  const [responsibleUserError, setResponsibleUserError] = useState(false);
  const [deadlineError, setDeadlineError] = useState(false);
  const [recurrenceCountError, setRecurrenceCountError] = useState(false);
  const [resultTypeError, setResultTypeError] = useState(false);
  const [resultCommentError, setResultCommentError] = useState(false);
  const [activityTitleError, setActivityTitleError] = useState(false);
  const [waitingComment, setWaitingComment] = useState("");
  const [waitingCommentError, setWaitingCommentError] = useState(false);

  const { activities } = useActivities();
  const {
    createActivity: mutateCreateActivity,
    updateActivity: mutateUpdateActivity,
    updateActivityStatus: mutateUpdateActivityStatus,
    deleteActivity: mutateDeleteActivity,
    isCreatingActivity,
    isUpdatingActivity,
    isUpdatingActivityStatus,
    isDeletingActivity,
    invalidateActivityQueries,
  } = useActivityMutations();
  const { members } = useMembers();
  const { allContactPersons: contactPersons } = useContactPersons();
  const selectableMembers: MembersFieldDto[] = useMemo(
    () =>
      members.map((member: MemberPartialDto) => ({
        memberId: member.memberId,
        firstname: member.firstname,
        lastname: member.lastname,
      })),
    [members]
  );
  const selectedContactPerson = useMemo(
    () => contactPersons.find((contactPerson) => contactPerson.contactPersonId === selectedContactPersonId) || null,
    [contactPersons, selectedContactPersonId]
  );

  const resetFormErrors = () => {
    setActivityTypeError(false);
    setContactPersonError(false);
    setResponsibleUserError(false);
    setDeadlineError(false);
    setRecurrenceCountError(false);
    setActivityTitleError(false);
    setResultTypeError(false);
    setResultCommentError(false);
    setWaitingCommentError(false);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedActivity(null);
    setDialogMode("create");
    setSelectedContactPersonId("");
    setActivityType(ActivityType.CALL);
    setActivityTitle("");
    setActivityDescription("");
    setSelectedResponsibleUserId("");
    setActivityDeadline(null);
    setActivityStatus(ActivityStatus.OPEN);
    setIsRecurring(false);
    setRecurrenceFrequency(ActivityRecurrenceFrequency.MONTHLY);
    setRecurrenceEndType(ActivityRecurrenceEndType.AFTER_N_OCCURRENCES);
    setRecurrenceCount(3);
    setResultType("");
    setResultComment("");
    setWaitingComment("");
    resetFormErrors();
  };

  const compareNullableStrings = (leftValue: string | null | undefined, rightValue: string | null | undefined) => {
    const left = leftValue?.trim() || "";
    const right = rightValue?.trim() || "";

    return left.localeCompare(right, "de", { sensitivity: "base" });
  };

  const getFilteredAndSorted = useCallback((): ActivityDto[] => {
    let filtered = activities.filter((activity) => {
      const isOverdue = dayjs(activity.deadline).isBefore(dayjs(), "day") && activity.status === ActivityStatus.OPEN;
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const searchableContent = [
        activity.title,
        activity.contactPersonName,
        activity.companyName,
        activity.responsibleUserName,
        getActivityTypeLabel(activity.activityType),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        (statusFilter.length === 0 || statusFilter.includes(activity.status)) &&
        (typeFilter.length === 0 || typeFilter.includes(activity.activityType)) &&
        (deadlineFilter.length === 0 || (deadlineFilter.includes("OVERDUE") && isOverdue)) &&
        (!normalizedSearch || searchableContent.includes(normalizedSearch))
      );
    });

    filtered = [...filtered].sort((a, b) => {
      if (sortBy === "deadlineAsc") {
        return dayjs(a.deadline).valueOf() - dayjs(b.deadline).valueOf();
      } else if (sortBy === "deadlineDesc") {
        return dayjs(b.deadline).valueOf() - dayjs(a.deadline).valueOf();
      } else if (sortBy === "updatedAtAsc") {
        return dayjs(a.updatedAt).valueOf() - dayjs(b.updatedAt).valueOf();
      } else if (sortBy === "updatedAtDesc") {
        return dayjs(b.updatedAt).valueOf() - dayjs(a.updatedAt).valueOf();
      } else if (sortBy === "titleAsc") {
        return compareNullableStrings(a.title, b.title);
      } else if (sortBy === "titleDesc") {
        return compareNullableStrings(b.title, a.title);
      } else if (sortBy === "responsibleUserNameAsc") {
        return compareNullableStrings(a.responsibleUserName, b.responsibleUserName);
      } else if (sortBy === "responsibleUserNameDesc") {
        return compareNullableStrings(b.responsibleUserName, a.responsibleUserName);
      }

      return 0;
    });

    return filtered;
  }, [activities, deadlineFilter, searchTerm, sortBy, statusFilter, typeFilter]);

  const toggleSort = (attribute: "deadline" | "updatedAt" | "title" | "responsibleUserName") => {
    setSortBy((prev) => {
      if (prev === `${attribute}Asc`) {
        return `${attribute}Desc`;
      } else if (prev === `${attribute}Desc`) {
        return "";
      } else {
        return `${attribute}Asc`;
      }
    });
  };

  const getSortIcon = (attribute: "deadline" | "updatedAt" | "title" | "responsibleUserName") => {
    switch (sortBy) {
      case `${attribute}Asc`:
        return <ExpandMore />;
      case `${attribute}Desc`:
        return <ExpandLess />;
      default:
        return <UnfoldMore />;
    }
  };

  const canManageActivity = (activity: ActivityDto) =>
    activity.responsibleUserId === auth.userID || hasActivityManagementPermission;

  const shouldShowActionColumn = getFilteredAndSorted().some(canManageActivity);

  const toggleFilters = () => {
    setShowFilters((previousValue) => !previousValue);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setTypeFilter([]);
    setDeadlineFilter([]);
    setStatusFilter([]);
  };

  const populateDialog = (activity: ActivityDto, mode: ActivityDialogMode) => {
    setSelectedActivity(activity);
    setDialogMode(mode);
    setSelectedContactPersonId(activity.contactPersonId);
    setActivityType(activity.activityType);
    setActivityTitle(activity.title);
    setActivityDescription(activity.description || "");
    setSelectedResponsibleUserId(activity.responsibleUserId);
    setActivityDeadline(dayjs(activity.deadline));
    setActivityStatus(activity.status);
    setIsRecurring(false);
    setRecurrenceFrequency(ActivityRecurrenceFrequency.MONTHLY);
    setRecurrenceEndType(ActivityRecurrenceEndType.AFTER_N_OCCURRENCES);
    setRecurrenceCount(3);
    setResultType(activity.resultType || "");
    setResultComment(activity.resultComment || "");
    setWaitingComment(activity.waitingComment || "");
    resetFormErrors();
    setDialogOpen(true);
  };

  const handleOpenCreateDialog = () => {
    setSelectedActivity(null);
    setDialogMode("create");
    setSelectedContactPersonId("");
    setActivityType(ActivityType.CALL);
    setActivityTitle("");
    setActivityDescription("");
    setSelectedResponsibleUserId(auth.userID ?? "");
    setActivityDeadline(dayjs().add(7, "day"));
    setActivityStatus(ActivityStatus.OPEN);
    setIsRecurring(false);
    setRecurrenceFrequency(ActivityRecurrenceFrequency.MONTHLY);
    setRecurrenceEndType(ActivityRecurrenceEndType.AFTER_N_OCCURRENCES);
    setRecurrenceCount(3);
    setResultType("");
    setResultComment("");
    setWaitingComment("");
    resetFormErrors();
    setDialogOpen(true);
  };

  const handleOpenDeleteDialog = (activity: ActivityDto) => {
    setSelectedActivity(activity);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedActivity(null);
  };

  const handleDeleteActivity = async () => {
    if (!selectedActivity) {
      return;
    }

    try {
      await mutateDeleteActivity(selectedActivity.activityId);
      await invalidateActivityQueries();
      showSuccessMessage("Aktivität erfolgreich gelöscht");
      setDeleteDialogOpen(false);
      setSelectedActivity(null);
    } catch {
      showErrorMessage("Aktivität konnte nicht gelöscht werden");
    }
  };

  const validateDialog = () => {
    let hasError = false;

    if (dialogMode === "create" && !selectedContactPersonId) {
      setContactPersonError(true);
      hasError = true;
    } else {
      setContactPersonError(false);
    }

    if (!activityType) {
      setActivityTypeError(true);
      hasError = true;
    } else {
      setActivityTypeError(false);
    }

    if (!selectedResponsibleUserId) {
      setResponsibleUserError(true);
      hasError = true;
    } else {
      setResponsibleUserError(false);
    }

    if (dialogMode !== "complete" && !activityTitle.trim()) {
      setActivityTitleError(true);
      hasError = true;
    } else {
      setActivityTitleError(false);
    }

    if (!activityDeadline) {
      setDeadlineError(true);
      hasError = true;
    } else {
      setDeadlineError(false);
    }

    if (
      dialogMode === "create" &&
      isRecurring &&
      recurrenceEndType === ActivityRecurrenceEndType.AFTER_N_OCCURRENCES &&
      recurrenceCount < 1
    ) {
      setRecurrenceCountError(true);
      hasError = true;
    } else {
      setRecurrenceCountError(false);
    }

    if (dialogMode === "complete") {
      if (!resultType) {
        setResultTypeError(true);
        hasError = true;
      } else {
        setResultTypeError(false);
      }

      if (!resultComment.trim()) {
        setResultCommentError(true);
        hasError = true;
      } else {
        setResultCommentError(false);
      }
    }

    if (dialogMode === "edit" && activityStatus === ActivityStatus.WAITING_FOR_CUSTOMER && !waitingComment.trim()) {
      setWaitingCommentError(true);
      hasError = true;
    } else if (dialogMode !== "complete") {
      setWaitingCommentError(false);
    }

    return !hasError;
  };

  const handleSaveActivity = async () => {
    if (!validateDialog()) {
      return;
    }

    if (dialogMode === "create") {
      if (!selectedContactPersonId || !selectedResponsibleUserId || !activityDeadline) {
        return;
      }

      const payload: CreateActivityDto = {
        contactPersonId: Number(selectedContactPersonId),
        responsibleUserId: Number(selectedResponsibleUserId),
        activityType,
        title: activityTitle.trim(),
        description: activityDescription.trim() || null,
        deadline: activityDeadline.toDate(),
        isRecurring,
        recurrenceFrequency: isRecurring ? recurrenceFrequency : null,
        recurrenceInterval: isRecurring ? 1 : null,
        recurrenceEndType: isRecurring ? ActivityRecurrenceEndType.AFTER_N_OCCURRENCES : null,
        recurrenceEndDate: null,
        recurrenceCount: isRecurring ? recurrenceCount : null,
      };

      try {
        await mutateCreateActivity(payload);
        await invalidateActivityQueries();
        showSuccessMessage("Aktivität erfolgreich erstellt");
        handleCloseDialog();
      } catch {
        showErrorMessage("Aktivität konnte nicht erstellt werden");
      }
      return;
    }

    if (!selectedActivity) {
      return;
    }

    if (dialogMode === "edit") {
      if (!activityDeadline) {
        return;
      }

      try {
        await mutateUpdateActivity({
          activityId: selectedActivity.activityId,
          payload: {
            activityType,
            title: activityTitle.trim(),
            description: activityDescription.trim() || null,
            responsibleUserId: Number(selectedResponsibleUserId),
            deadline: activityDeadline.toDate(),
            isRecurring: selectedActivity.isRecurring,
            recurrenceFrequency: selectedActivity.recurrenceFrequency,
            recurrenceInterval: selectedActivity.recurrenceInterval,
            recurrenceEndType: selectedActivity.recurrenceEndType,
            recurrenceEndDate: selectedActivity.recurrenceEndDate,
            recurrenceCount: selectedActivity.recurrenceCount,
          },
        });

        const nextWaitingComment =
          activityStatus === ActivityStatus.WAITING_FOR_CUSTOMER ? waitingComment.trim() || null : null;
        const statusChanged =
          activityStatus !== selectedActivity.status || nextWaitingComment !== selectedActivity.waitingComment;

        if (statusChanged) {
          await mutateUpdateActivityStatus({
            activityId: selectedActivity.activityId,
            payload: {
              status: activityStatus,
              resultType: null,
              resultComment: null,
              waitingComment: nextWaitingComment,
            },
          });
        }

        await invalidateActivityQueries();
        showSuccessMessage("Aktivität erfolgreich aktualisiert");
        handleCloseDialog();
      } catch {
        showErrorMessage("Aktivität konnte nicht aktualisiert werden");
      }
      return;
    }

    try {
      await mutateUpdateActivityStatus({
        activityId: selectedActivity.activityId,
        payload: {
          status: ActivityStatus.DONE,
          resultType: resultType || null,
          resultComment: resultComment.trim() || null,
          waitingComment: null,
        },
      });
      await invalidateActivityQueries();
      showSuccessMessage("Aktivität erfolgreich abgeschlossen");
      handleCloseDialog();
    } catch {
      showErrorMessage("Aktivität konnte nicht abgeschlossen werden");
    }
  };

  const isSaving = isCreatingActivity || isUpdatingActivity || isUpdatingActivityStatus;

  return (
    <div>
      <Box sx={{ marginBottom: "10px" }}>
        <ActivityAddButton onClick={handleOpenCreateDialog} />
      </Box>
      <Paper sx={styles.filterContainer}>
        <StyledForm noValidate autoComplete="off">
          <Box sx={styles.filterToolbar}>
            <Box sx={styles.filterActions}>
              <SearchField
                id="activity-search-filter"
                label="Suche"
                variant="standard"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={styles.searchField}
              />
              <IconButton aria-label="Aktivitätsfilter umschalten" onClick={toggleFilters} size="large">
                {showFilters ? <FilterListOff /> : <FilterList />}
              </IconButton>
              {showFilters && (
                <Button variant="outlined" startIcon={<RestartAlt />} onClick={resetFilters}>
                  Filter zurücksetzen
                </Button>
              )}
            </Box>
            {showFilters && (
              <Box sx={styles.filterPanel}>
                <Box sx={styles.filterSection}>
                  <Typography variant="body2" sx={styles.filterLabel}>
                    Typ
                  </Typography>
                  <FormControl sx={styles.filterOptions}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={typeFilter.includes(ActivityType.CALL)}
                          onChange={(event) =>
                            setTypeFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityType.CALL]
                                : currentValue.filter((value) => value !== ActivityType.CALL)
                            )
                          }
                        />
                      }
                      label="Anruf"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={typeFilter.includes(ActivityType.MAIL)}
                          onChange={(event) =>
                            setTypeFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityType.MAIL]
                                : currentValue.filter((value) => value !== ActivityType.MAIL)
                            )
                          }
                        />
                      }
                      label="E-Mail"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={typeFilter.includes(ActivityType.MEETING)}
                          onChange={(event) =>
                            setTypeFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityType.MEETING]
                                : currentValue.filter((value) => value !== ActivityType.MEETING)
                            )
                          }
                        />
                      }
                      label="Meeting"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={typeFilter.includes(ActivityType.OTHER)}
                          onChange={(event) =>
                            setTypeFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityType.OTHER]
                                : currentValue.filter((value) => value !== ActivityType.OTHER)
                            )
                          }
                        />
                      }
                      label="Sonstiges"
                    />
                  </FormControl>
                </Box>
                <Box sx={styles.filterSection}>
                  <Typography variant="body2" sx={styles.filterLabel}>
                    Fälligkeit
                  </Typography>
                  <FormControl sx={styles.filterOptions}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={deadlineFilter.includes("OVERDUE")}
                          onChange={(event) =>
                            setDeadlineFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, "OVERDUE"]
                                : currentValue.filter((value) => value !== "OVERDUE")
                            )
                          }
                        />
                      }
                      label="Überfällig"
                    />
                  </FormControl>
                </Box>
                <Box sx={styles.filterSection}>
                  <Typography variant="body2" sx={styles.filterLabel}>
                    Status
                  </Typography>
                  <FormControl sx={styles.filterOptions}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusFilter.includes(ActivityStatus.OPEN)}
                          onChange={(event) =>
                            setStatusFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityStatus.OPEN]
                                : currentValue.filter((value) => value !== ActivityStatus.OPEN)
                            )
                          }
                        />
                      }
                      label="Offen"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusFilter.includes(ActivityStatus.WAITING_FOR_CUSTOMER)}
                          onChange={(event) =>
                            setStatusFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityStatus.WAITING_FOR_CUSTOMER]
                                : currentValue.filter((value) => value !== ActivityStatus.WAITING_FOR_CUSTOMER)
                            )
                          }
                        />
                      }
                      label="Wartet auf Kunde"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={statusFilter.includes(ActivityStatus.DONE)}
                          onChange={(event) =>
                            setStatusFilter((currentValue) =>
                              event.target.checked
                                ? [...currentValue, ActivityStatus.DONE]
                                : currentValue.filter((value) => value !== ActivityStatus.DONE)
                            )
                          }
                        />
                      }
                      label="Erledigt"
                    />
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        </StyledForm>
        <Box sx={styles.amountOfEntries}>{`${getFilteredAndSorted().length} Einträge`}</Box>
      </Paper>
      <TableContainer component={Paper} sx={styles.tableContainer}>
        <Table stickyHeader aria-label="activities table">
          <TableHead>
            <TableRow>
              <TableCell sx={styles.tableHeadCell}>Aktivitätstyp</TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <Box sx={styles.tableHeadSortBtn} onClick={() => toggleSort("title")}>
                  <>
                    Titel
                    {getSortIcon("title")}
                  </>
                </Box>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>Ansprechpartner</TableCell>
              <TableCell sx={styles.tableHeadCell}>Unternehmen</TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <Box sx={styles.tableHeadSortBtn} onClick={() => toggleSort("responsibleUserName")}>
                  <>
                    Verantwortliche Person
                    {getSortIcon("responsibleUserName")}
                  </>
                </Box>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <Box sx={styles.tableHeadSortBtn} onClick={() => toggleSort("deadline")}>
                  <>
                    Deadline
                    {getSortIcon("deadline")}
                  </>
                </Box>
              </TableCell>
              <TableCell sx={styles.tableHeadCell}>Status</TableCell>
              <TableCell sx={styles.tableHeadCell}>
                <Box sx={styles.tableHeadSortBtn} onClick={() => toggleSort("updatedAt")}>
                  <>
                    Letzte Änderung
                    {getSortIcon("updatedAt")}
                  </>
                </Box>
              </TableCell>
              {shouldShowActionColumn && <TableCell sx={styles.tableHeadCell}>Aktionen</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredAndSorted().length === 0 ? (
              <TableRow>
                <TableCell colSpan={shouldShowActionColumn ? 9 : 8}>Keine Aktivitäten vorhanden.</TableCell>
              </TableRow>
            ) : (
              getFilteredAndSorted().map((activity) => (
                <TableRow
                  hover
                  key={activity.activityId}
                  onClick={() => navigate(`/aktivitaeten/${activity.activityId}`)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{getActivityTypeLabel(activity.activityType)}</TableCell>
                  <TableCell>
                    <Typography color="secondary">
                      <StyledLink
                        to={`/aktivitaeten/${activity.activityId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {activity.title}
                      </StyledLink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="secondary">
                      <StyledLink
                        to={`/ansprechpersonen/${activity.contactPersonId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {activity.contactPersonName}
                      </StyledLink>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography color="secondary">
                      <StyledLink
                        to={`/unternehmen/${activity.companyId}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {activity.companyName}
                      </StyledLink>
                    </Typography>
                  </TableCell>
                  <TableCell>{activity.responsibleUserName}</TableCell>
                  <TableCell>{dayjs(activity.deadline).format("DD.MM.YYYY")}</TableCell>
                  <TableCell>
                    <ActivityStatusChip status={activity.status} />
                  </TableCell>
                  <TableCell>{dayjs(activity.updatedAt).format("DD.MM.YYYY HH:mm")}</TableCell>
                  {shouldShowActionColumn && (
                    <TableCell sx={styles.actionCell}>
                      {canManageActivity(activity) && (
                        <Box sx={styles.actionButtons}>
                          <ActivityActionButtons
                            isDone={activity.status === ActivityStatus.DONE}
                            onComplete={() => populateDialog(activity, "complete")}
                            onEdit={() => populateDialog(activity, "edit")}
                            onDelete={() => handleOpenDeleteDialog(activity)}
                            stopPropagation
                          />
                        </Box>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {dialogMode === "complete" ? (
        <ActivityCompleteDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          onSubmit={handleSaveActivity}
          isSubmitting={isSaving}
          resultType={resultType}
          onResultTypeChange={(value) => {
            setResultType(value);
            setResultTypeError(false);
          }}
          resultComment={resultComment}
          onResultCommentChange={(value) => {
            setResultComment(value);
            if (value.trim()) {
              setResultCommentError(false);
            }
          }}
          resultTypeError={resultTypeError}
          resultCommentError={resultCommentError}
        />
      ) : (
        <ActivityFormDialog
          open={dialogOpen}
          title={dialogMode === "create" ? "Aktivität hinzufügen" : "Aktivität bearbeiten"}
          submitLabel={dialogMode === "create" ? "Aktivität erstellen" : "Änderungen speichern"}
          onClose={handleCloseDialog}
          onSubmit={handleSaveActivity}
          isSubmitting={isSaving}
          contactPersonOptions={dialogMode === "create" ? contactPersons : undefined}
          selectedContactPersonId={dialogMode === "create" ? selectedContactPersonId : undefined}
          onSelectedContactPersonChange={
            dialogMode === "create"
              ? (value) => {
                  setSelectedContactPersonId(value);
                  setContactPersonError(false);
                }
              : undefined
          }
          contactPersonError={dialogMode === "create" ? contactPersonError : undefined}
          contactPersonName={dialogMode === "edit" ? selectedActivity?.contactPersonName || "" : undefined}
          companyName={
            dialogMode === "create" ? selectedContactPerson?.companyName || "" : selectedActivity?.companyName || ""
          }
          activityType={activityType}
          onActivityTypeChange={(value) => {
            setActivityType(value);
            setActivityTypeError(false);
          }}
          activityTypeError={activityTypeError}
          activityTitle={activityTitle}
          onActivityTitleChange={(value) => {
            setActivityTitle(value);
            setActivityTitleError(false);
          }}
          activityTitleError={activityTitleError}
          activityDescription={activityDescription}
          onActivityDescriptionChange={setActivityDescription}
          responsibleUserId={selectedResponsibleUserId}
          onResponsibleUserChange={(value) => {
            setSelectedResponsibleUserId(value);
            setResponsibleUserError(false);
          }}
          responsibleUserError={responsibleUserError}
          responsibleUserLabel="Verantwortliche Person"
          members={selectableMembers}
          activityDeadline={activityDeadline}
          onActivityDeadlineChange={(value) => {
            setActivityDeadline(value);
            setDeadlineError(value === null);
          }}
          deadlineError={deadlineError}
          idPrefix={dialogMode === "create" ? "activity-create" : "activity-edit"}
          createRecurrenceOptions={
            dialogMode === "create"
              ? {
                  isRecurring,
                  onIsRecurringChange: setIsRecurring,
                  recurrenceFrequency,
                  onRecurrenceFrequencyChange: setRecurrenceFrequency,
                  recurrenceCount,
                  onRecurrenceCountChange: setRecurrenceCount,
                  recurrenceCountError,
                }
              : undefined
          }
          statusOptions={
            dialogMode === "edit"
              ? {
                  value: activityStatus,
                  onChange: (value) => {
                    setActivityStatus(value);
                    if (value !== ActivityStatus.WAITING_FOR_CUSTOMER) {
                      setWaitingComment("");
                      setWaitingCommentError(false);
                    }
                  },
                  options:
                    selectedActivity?.status === ActivityStatus.DONE
                      ? [{ value: ActivityStatus.DONE, label: "Erledigt" }]
                      : [
                          { value: ActivityStatus.OPEN, label: "Offen" },
                          { value: ActivityStatus.WAITING_FOR_CUSTOMER, label: "Wartet auf Kunde" },
                        ],
                  disabled: selectedActivity?.status === ActivityStatus.DONE,
                }
              : undefined
          }
          waitingReasonOptions={
            dialogMode === "edit"
              ? {
                  value: waitingComment,
                  onChange: (value) => {
                    setWaitingComment(value);
                    if (value.trim()) {
                      setWaitingCommentError(false);
                    }
                  },
                  error: waitingCommentError,
                }
              : undefined
          }
        />
      )}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteActivity}
        cofirmButtonColor="error"
        title="Aktivität löschen"
        content={`Möchtest du die Aktivität${
          selectedActivity?.title ? ` "${selectedActivity.title}"` : ""
        } wirklich löschen?`}
        confirmButtonText={isDeletingActivity ? "Lösche..." : "Löschen"}
      />
    </div>
  );
};

export default ActivityOverview;
